/**
 * feature-manifests — aggregator of the TS registry manifests that feed the
 * self-describing feature catalog (Design A/C, `pnpm gen:features` bridge).
 *
 * The `@space/shared` TS registries (country packs today; meeting providers,
 * learning blocks and modules as soon as the F2-A/F2-B agents publish them)
 * are NOT redefined in Rust: they emit a generated JSON (`feature-manifest.gen.json`)
 * that the server embeds and reconciles at boot (`core_rust::platform::catalog::
 * include_manifest_file`).
 *
 * EXTENSIBILITY (recipe for later agents): adding a source = **1 line**
 * in `allManifestSources()` (import the manifest array your registry already
 * exposes, with its `category`). Then `pnpm gen:features` regenerates the JSON; the
 * determinism/freshness test fails if you forget to regenerate it.
 */

import { completionCriterionManifests } from "./academy/completion/registry";
import { countryPackManifests, type FeatureManifest } from "./academy/country/packs";
import { dataAccessManifests } from "./academy/data-access";
import { entitlementManifests } from "./academy/entitlement/registry";
import { freeTierManifests } from "./academy/free/registry";
import { gradingSchemeManifests } from "./academy/grading/registry";
import { assignmentModuleManifest } from "./academy/modules/assignment-plugin";
import { guardianConsentManifests } from "./academy/people/consent-registry";
import { courseModuleManifests } from "./academy/modules/plugins";
import { progressionScopeManifests } from "./academy/progression/registry";
import { verticalManifests } from "./academy/verticals";
import { mediaProviderManifests } from "./live";
import { videoBlockManifest } from "./media";

/** Catalog categories (== `Category` serde snake_case from the Rust core). */
export type ManifestCategory =
  | "payment_method"
  | "integration"
  | "module_kind"
  | "meeting_provider"
  | "learning_block"
  | "flag"
  | "admin_tool"
  | "io_entity"
  | "country_pack"
  | "grading_scheme"
  | "vertical";

/** A group of manifests from a single registry, with the catalog category. */
export interface ManifestSource {
  /** Stable id of the source (grouping/drift diagnostics). */
  sourceId: string;
  /** Catalog category these manifests belong to. */
  category: ManifestCategory;
  /** Manifests contributed by the registry. */
  manifests: FeatureManifest[];
}

/**
 * All TS manifest sources. **Extension point**: each agent adds ONE entry
 * here, importing its registry's export. Examples (commented out
 * until they exist on master):
 *
 *   { sourceId: "meeting_providers", category: "meeting_provider",
 *     manifests: mediaProviderManifests() },              // F2-B
 *   { sourceId: "video_block", category: "learning_block",
 *     manifests: [videoBlockManifest()] },                // F2-B
 *   { sourceId: "course_modules", category: "module_kind",
 *     manifests: courseModuleManifests() },               // F2-A
 */
export function allManifestSources(): ManifestSource[] {
  return [
    {
      sourceId: "country_packs",
      category: "country_pack",
      manifests: countryPackManifests(),
    },
    // F2-B: live-class providers (media.provider.<id>) and the video block.
    {
      sourceId: "meeting_providers",
      category: "meeting_provider",
      manifests: mediaProviderManifests(),
    },
    {
      sourceId: "video_block",
      category: "learning_block",
      manifests: [videoBlockManifest()],
    },
    // F2-A/F3-A/F3-B: module kinds (lesson, resource, live_session, assignment).
    {
      sourceId: "course_modules",
      category: "module_kind",
      manifests: [...courseModuleManifests(), assignmentModuleManifest()],
    },
    // F3-B: grading scales (ve-0-20, pct-100, letter, pass-fail).
    {
      sourceId: "grading_schemes",
      category: "grading_scheme",
      manifests: gradingSchemeManifests(),
    },
    // F3-C: the `forum` module kind (academy.module.forum) already travels in
    // `courseModuleManifests()` (defaultModuleManifests includes FORUM).
    // FLAGS: product verticals (billing/crm/gamification/recordings) —
    // the "umbrella" switches for the progressive per-academy rollout.
    {
      sourceId: "academy_verticals",
      category: "vertical",
      manifests: verticalManifests(),
    },
    // M1: self-describing completion criteria (academy.completion.<key>).
    {
      sourceId: "completion_criteria",
      category: "flag",
      manifests: completionCriterionManifests(),
    },
    // sync→REST migration (plan 06): per-stage rollback levers for the
    // client's read path. They do NOT gate access control — see the module.
    {
      sourceId: "data_access",
      category: "flag",
      manifests: dataAccessManifests(),
    },
    // B1 · entitlement (plan 01): what happens if the student hasn't paid, in
    // a platform→org→program→course→cohort→student cascade.
    {
      sourceId: "entitlement",
      category: "vertical",
      manifests: entitlementManifests(),
    },
    // B6 · levels (plan 04): stage ORIGINS self-declare as
    // features (`academy.progression.scope.<key>`), exactly like the
    // completion criteria. Adding a new origin doesn't touch this file.
    {
      sourceId: "progression_scopes",
      category: "flag",
      manifests: progressionScopeManifests(),
    },
    // B5 · "free", workshops and funnel (gap analysis §N): sample class and
    // automatic acquisition. Free content has NO flag of its own — it's the
    // absence of the payment obligation that entitlement already models.
    {
      sourceId: "free_tier",
      category: "vertical",
      manifests: freeTierManifests(),
    },
    // P41 · guardian consent: the record and its teeth. The
    // age THRESHOLD is deliberately NOT here — it's academy configuration
    // (`app_config` → `academy.guardian_consent_age`), not a switch.
    {
      sourceId: "guardian_consent",
      category: "flag",
      manifests: guardianConsentManifests(),
    },
  ];
}

// ── Bridge with the Rust core (`FeatureManifestFile` serde, camelCase) ───────────

/** Version of the generated file's format (== `FeatureManifestFile.version`). */
export const MANIFEST_FILE_VERSION = 1;

/** Path of the generated JSON, relative to the repo root. */
export const MANIFEST_GEN_RELATIVE_PATH = "packages/shared/src/feature-manifest.gen.json";

/** Structured implications (== `FeatureImplications` serde from the Rust core). */
export interface ManifestImplications {
  navHidden?: string[];
  flowsBlocked?: string[];
  dataPolicy: "preserved";
}

/** Descriptor in the Rust core's serde shape (`FeatureDescriptor`, camelCase). */
export interface ManifestDescriptor {
  key: string;
  name: string;
  description: string;
  category: ManifestCategory;
  scope: "platform" | "org" | "course";
  status: "stable" | "beta" | "stub" | "deprecated";
  configSchema?: unknown;
  docsUrl?: string;
  /** Dependency clauses (FLAGS): `"key"`, `"a|b|c"`, `?` suffix = soft. */
  requires?: string[];
  conflicts?: string[];
  gateable?: boolean;
  /**
   * Factory state with no row in `feature_flags` and no override. OMITTED when
   * it's `true` (the implicit default) to keep the generated JSON clean; only
   * features born disabled declare it.
   */
  defaultEnabled?: boolean;
  since?: string;
  implications?: ManifestImplications;
}

export interface ManifestFileSourceEntry {
  sourceId: string;
  descriptors: ManifestDescriptor[];
}

export interface FeatureManifestFile {
  version: number;
  /** Always `null`: determinism (no timestamps → byte-identical output). */
  generatedAt: null;
  sources: ManifestFileSourceEntry[];
}

/** The Rust core only knows stable|beta|stub|deprecated; `experimental` → `beta`. */
function mapStatus(status: FeatureManifest["status"]): ManifestDescriptor["status"] {
  return status === "experimental" ? "beta" : status;
}

/**
 * Adapts a registry key to the catalog convention `^[a-z_]+\.[a-z0-9_.]+$`
 * (== `core_rust::platform::catalog::key_is_valid`). Some registries use
 * hyphenated slugs (e.g. `media.provider.custom_link`); normalization is kept for robustness against future hyphenated slugs;
 * the bridge normalizes them to `_` so the embedded catalog validates without drift.
 * Deterministic and idempotent.
 */
export function normalizeCatalogKey(key: string): string {
  return key.replaceAll(/[^a-z0-9_.]/g, "_");
}

/**
 * Normalizes a `requires` CLAUSE (`"a|b?|c"`): each alternative is normalized
 * as a key while preserving the `|` separators and the `?` soft marker.
 */
function normalizeRequireClause(clause: string): string {
  return clause
    .split("|")
    .map((alt) => {
      const soft = alt.endsWith("?");
      const key = normalizeCatalogKey(soft ? alt.slice(0, -1) : alt);
      return soft ? `${key}?` : key;
    })
    .join("|");
}

function toDescriptor(m: FeatureManifest, category: ManifestCategory): ManifestDescriptor {
  // FIXED field order (serialization determinism).
  const d: ManifestDescriptor = {
    key: normalizeCatalogKey(m.key),
    name: m.name,
    description: m.description,
    category,
    scope: m.scope,
    status: mapStatus(m.status),
  };
  if (m.docsSlug) {
    d.docsUrl = `/help/${m.docsSlug}`;
  }
  if (m.requires && m.requires.length > 0) {
    d.requires = m.requires.map(normalizeRequireClause);
  }
  if (m.conflicts && m.conflicts.length > 0) {
    d.conflicts = m.conflicts.map(normalizeCatalogKey);
  }
  if (m.gateable) {
    d.gateable = true;
  }
  if (m.defaultEnabled === false) {
    d.defaultEnabled = false;
  }
  if (m.since) {
    d.since = m.since;
  }
  if (m.implications) {
    const impl: ManifestImplications = { dataPolicy: "preserved" };
    if (m.implications.navHidden && m.implications.navHidden.length > 0) {
      impl.navHidden = m.implications.navHidden;
    }
    if (m.implications.flowsBlocked && m.implications.flowsBlocked.length > 0) {
      impl.flowsBlocked = m.implications.flowsBlocked;
    }
    d.implications = impl;
  }
  return d;
}

/**
 * Builds the manifest file from all sources. Deterministic:
 * no timestamps and descriptors sorted by `key` within each
 * source.
 */
function byKey(a: ManifestDescriptor, b: ManifestDescriptor): number {
  // Code-point comparison (deterministic, locale-independent).
  if (a.key < b.key) {
    return -1;
  }
  if (a.key > b.key) {
    return 1;
  }
  return 0;
}

export function buildFeatureManifestFile(): FeatureManifestFile {
  const sources: ManifestFileSourceEntry[] = allManifestSources().map((src) => ({
    sourceId: src.sourceId,
    descriptors: src.manifests.map((m) => toDescriptor(m, src.category)).toSorted(byKey),
  }));
  return { version: MANIFEST_FILE_VERSION, generatedAt: null, sources };
}

/**
 * Canonical serialization (2 spaces + trailing newline) used by both the generator
 * (`pnpm gen:features`) and the freshness test. Key order is the objects'
 * insertion order → stable.
 */
export function serializeFeatureManifestFile(): string {
  return `${JSON.stringify(buildFeatureManifestFile(), null, 2)}\n`;
}
